import { type Router, type RouteLocationNormalized } from 'vue-router'
import { onCLS, onINP, onLCP, type Metric } from 'web-vitals'

type CaptureProperties = Record<string, string | number | boolean | null | undefined>
type CaptureFn = (event: string, properties?: CaptureProperties) => void

type InitPerformanceInstrumentationOptions = {
  router: Router
  capture: CaptureFn
}

function getNetworkType() {
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } })
    .connection
  if (!connection) return undefined
  return connection.effectiveType
}

function getRouteLabel(route: RouteLocationNormalized) {
  if (typeof route.name === 'string') return route.name
  return route.path
}

function captureWebVital(capture: CaptureFn, metric: Metric) {
  capture('perf_web_vital', {
    metric_name: metric.name,
    metric_value: metric.value,
    metric_delta: metric.delta,
    metric_id: metric.id,
    metric_rating: metric.rating,
    route: window.location.pathname,
    network_type: getNetworkType(),
  })
}

export function initPerformanceInstrumentation(options: InitPerformanceInstrumentationOptions) {
  let pendingRouteChange: {
    from: string
    to: string
    startedAt: number
  } | null = null

  const { router, capture } = options

  onLCP((metric) => {
    captureWebVital(capture, metric)
  })

  onINP((metric) => {
    captureWebVital(capture, metric)
  })

  onCLS((metric) => {
    captureWebVital(capture, metric)
  })

  router.beforeEach((to, from) => {
    if (to.fullPath === from.fullPath) return

    pendingRouteChange = {
      from: getRouteLabel(from),
      to: getRouteLabel(to),
      startedAt: performance.now(),
    }
  })

  router.afterEach((to, _from, failure) => {
    if (failure) {
      pendingRouteChange = null
      return
    }

    if (!pendingRouteChange) return
    if (pendingRouteChange.to !== getRouteLabel(to)) {
      pendingRouteChange = null
      return
    }

    const endedAt = performance.now()
    const routeChangeDurationMs = endedAt - pendingRouteChange.startedAt

    capture('perf_route_change', {
      from_route: pendingRouteChange.from,
      to_route: pendingRouteChange.to,
      route_change_duration_ms: Math.round(routeChangeDurationMs),
      network_type: getNetworkType(),
    })

    pendingRouteChange = null
  })

  router.onError((error, to, from) => {
    const message = error instanceof Error ? error.message : String(error)

    if (
      !message.includes('Failed to fetch dynamically imported module') &&
      !message.includes('ChunkLoadError')
    ) {
      return
    }

    capture('perf_chunk_load_error', {
      from_route: from ? getRouteLabel(from) : undefined,
      to_route: to ? getRouteLabel(to) : undefined,
      error_message: message,
      network_type: getNetworkType(),
    })
  })

  const captureFirstLoad = () => {
    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
    const firstContentfulPaint = performance
      .getEntriesByType('paint')
      .find((entry) => entry.name === 'first-contentful-paint')

    if (!navigation) return

    capture('perf_first_load', {
      route: window.location.pathname,
      ttfb_ms: Math.round(navigation.responseStart),
      dom_content_loaded_ms: Math.round(navigation.domContentLoadedEventEnd),
      load_event_ms: Math.round(navigation.loadEventEnd),
      first_contentful_paint_ms: firstContentfulPaint
        ? Math.round(firstContentfulPaint.startTime)
        : undefined,
      network_type: getNetworkType(),
    })
  }

  if (document.readyState === 'complete') {
    requestAnimationFrame(captureFirstLoad)
  } else {
    window.addEventListener('load', () => setTimeout(captureFirstLoad, 0), { once: true })
  }
}
